import { useState, useEffect } from "react";
import { LifeBuoy, Mail, Loader2, CheckCircle2, ImagePlus, MessageSquare, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, usd, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch, Panel, StatusBadge, Table, Empty } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DLG = "bg-[#121722] border-[#242F42] text-white max-h-[90vh] overflow-y-auto";
const METHODS = [["", "Select method"], ["bank", "Bank Transfer"], ["upi", "UPI"], ["usdt_trc20", "USDT (TRC20)"], ["usdt_bep20", "USDT (BEP20)"]];

export const ReviewNotice = ({ hours = 4, testid = "deposit-review-notice" }) => (
  <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3" data-testid={testid}>
    <Clock size={16} className="mt-0.5 shrink-0 text-amber-400" />
    <p className="text-sm text-amber-200/90">Your deposit request is under review. Please allow up to {hours} hours for manual verification.</p>
  </div>
);

export function TicketForm({ open, prefill, onClose, onCreated }) {
  const { user } = useAuth();
  const { data: cats } = useFetch("/tickets/categories");
  const [f, setF] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => {
    if (open) {
      setF({ amount: prefill?.amount || "", method: prefill?.method || "", txn_ref: prefill?.txn_id || "", category: "", description: "" });
      setFile(null);
      setDone(null);
    }
  }, [open, prefill]);
  if (!open) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (file && file.size > 5 * 1024 * 1024) return toast.error("Screenshot must be 5 MB or smaller");
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => v !== "" && fd.append(k, v));
    if (prefill?.id) fd.append("deposit_id", prefill.id);
    if (file) fd.append("screenshot", file);
    try {
      const { data } = await api.post("/tickets", fd);
      setDone(data);
      onCreated?.();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${DLG} max-w-xl`} data-testid="ticket-form-dialog">
        <DialogHeader><DialogTitle className="font-display">Need help with your deposit?</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-4 py-4 text-center" data-testid="ticket-confirmation">
            <CheckCircle2 size={40} className="mx-auto text-[#10B981]" />
            <p className="text-lg font-semibold">Ticket submitted</p>
            <p className="text-sm text-slate-400">Your ticket ID is</p>
            <p className="num text-2xl font-semibold text-[#10B981]" data-testid="ticket-confirmation-id">{done.ticket_code}</p>
            <p className="text-xs text-slate-500">Our team will review it and reply here. You can follow it under "My support tickets".</p>
            <button className="nx-btn-primary w-full" onClick={onClose} data-testid="ticket-confirmation-close">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="ticket-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="nx-label">User ID</label><input className="nx-input num opacity-70" readOnly value={user?.user_code || ""} data-testid="ticket-user-id" /></div>
              <div><label className="nx-label">Email</label><input className="nx-input opacity-70" readOnly value={user?.email || ""} data-testid="ticket-email" /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="nx-label">Deposit amount (USD)</label><input type="number" step="0.01" min="0" required className="nx-input num" value={f.amount} onChange={set("amount")} data-testid="ticket-amount-input" /></div>
              <div><label className="nx-label">Payment method</label>
                <select required className="nx-input" value={f.method} onChange={set("method")} data-testid="ticket-method-select">
                  {METHODS.map(([v, l]) => <option key={v} value={v} disabled={!v}>{l}</option>)}
                </select></div>
            </div>
            <div><label className="nx-label">UTR / Transaction ID</label><input required className="nx-input num" value={f.txn_ref} onChange={set("txn_ref")} data-testid="ticket-txn-input" /></div>
            <div><label className="nx-label">Issue category</label>
              <select required className="nx-input" value={f.category} onChange={set("category")} data-testid="ticket-category-select">
                <option value="" disabled>Select a category</option>
                {Object.entries(cats || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></div>
            <div><label className="nx-label">Describe the problem</label>
              <textarea required minLength={10} rows={4} className="nx-input" value={f.description} onChange={set("description")} placeholder="When did you pay, what happened, anything else we should know" data-testid="ticket-description-input" /></div>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-[#334155] bg-[#0B0E14] px-4 py-3 text-sm text-slate-400 hover:border-[#10B981]/50">
              <ImagePlus size={18} className="text-[#10B981]" />
              <span className="truncate" data-testid="ticket-file-name">{file ? file.name : "Attach payment screenshot (optional · PNG/JPG/WEBP, max 5 MB)"}</span>
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ticket-screenshot-input" />
            </label>
            <button className="nx-btn-primary w-full py-3" disabled={busy} data-testid="ticket-submit-btn">{busy && <Loader2 size={16} className="animate-spin" />}Submit ticket</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function useAttachment(url, enabled) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let blobUrl;
    setSrc(null);
    if (enabled) {
      api.get(url, { responseType: "blob" }).then((r) => { blobUrl = URL.createObjectURL(r.data); setSrc(blobUrl); }).catch(() => setSrc(false));
    }
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [url, enabled]);
  return src;
}

export const Thread = ({ messages }) => (
  <div className="max-h-72 space-y-3 overflow-y-auto rounded-xl border border-[#1E2738] bg-[#0B0E14] p-4" data-testid="ticket-thread">
    {messages?.map((m) => (
      <div key={m.id} className={`flex ${m.from === "admin" ? "justify-start" : "justify-end"}`}>
        <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.from === "admin" ? "border border-[#10B981]/30 bg-[#10B981]/10 text-slate-100" : "bg-[#1A2232] text-slate-200"}`}>
          <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">{m.from === "admin" ? "Support team" : "Customer"} · {fmtDate(m.at)}</p>
          <p className="whitespace-pre-wrap">{m.text}</p>
        </div>
      </div>
    ))}
  </div>
);

function UserTicket({ id, onClose, onChanged }) {
  const { data: t, reload } = useFetch(`/tickets/${id}`);
  const img = useAttachment(`/tickets/${id}/attachment`, !!t?.has_attachment);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/tickets/${id}/reply`, { text });
      setText("");
      reload();
      onChanged();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${DLG} max-w-xl`} data-testid="user-ticket-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-3"><span className="num">{t?.ticket_code}</span>{t && <StatusBadge status={t.status} />}</DialogTitle></DialogHeader>
        {t && (
          <div className="space-y-4">
            <p className="text-xs text-slate-400">{t.category_label} · {usd(t.amount)} · {t.method_label} · <span className="num">{t.txn_ref}</span></p>
            {img && <img src={img} alt="Attached screenshot" className="max-h-56 rounded-lg border border-[#1E2738] object-contain" data-testid="user-ticket-attachment" />}
            <Thread messages={t.messages} />
            {t.status !== "closed" && (
              <form onSubmit={send} className="flex gap-2">
                <input required className="nx-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply" data-testid="user-ticket-reply-input" />
                <button className="nx-btn-primary" disabled={busy} data-testid="user-ticket-reply-btn">Send</button>
              </form>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DepositSupport({ prefill, onPrefillUsed }) {
  const { data: s } = useFetch("/public/settings");
  const { data: tickets, reload } = useFetch("/tickets");
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  useEffect(() => {
    if (prefill) setOpen(true);
  }, [prefill]);
  const close = () => { setOpen(false); onPrefillUsed?.(); };
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2" data-testid="deposit-support-section">
        <button onClick={() => setOpen(true)} className="nx-card flex items-start gap-4 p-5 text-left transition-colors hover:border-[#10B981]/40" data-testid="need-help-deposit-btn">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#10B981]/10 text-[#10B981]"><LifeBuoy size={19} /></span>
          <div><p className="font-semibold text-white">Need help with your deposit?</p>
            <p className="mt-1 text-xs text-slate-400">Submit a support ticket with your payment details and screenshot. You'll get a ticket ID to track it.</p></div>
        </button>
        <a href={`mailto:${s?.support_email || ""}?subject=${encodeURIComponent("Deposit support request")}`} className="nx-card flex items-start gap-4 p-5 transition-colors hover:border-[#10B981]/40" data-testid="contact-support-email-btn">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#1A2232] text-slate-300"><Mail size={19} /></span>
          <div><p className="font-semibold text-white">Contact Support via Email</p>
            <p className="num mt-1 text-xs text-[#10B981]" data-testid="support-email-display">{s?.support_email}</p></div>
        </a>
      </div>
      {!!tickets?.length && (
        <Panel title="My support tickets">
          <Table head={["Ticket ID", "Category", "Amount", "Status", "Updated", ""]} testid="my-tickets-table">
            {!tickets.length && <Empty cols={6} />}
            {tickets.map((t) => (
              <tr key={t.id} data-testid={`my-ticket-row-${t.ticket_code}`}>
                <td className="nx-td num text-[#10B981]">{t.ticket_code}</td>
                <td className="nx-td">{t.category_label}</td>
                <td className="nx-td num">{t.amount ? usd(t.amount) : "—"}</td>
                <td className="nx-td"><StatusBadge status={t.status} />{t.unread_by_user && <span className="ml-2 text-[10px] text-[#10B981]">New reply</span>}</td>
                <td className="nx-td text-xs">{fmtDate(t.updated_at)}</td>
                <td className="nx-td text-right"><button onClick={() => setView(t.id)} className="inline-flex items-center gap-1 text-xs text-[#10B981] hover:underline" data-testid={`view-ticket-${t.ticket_code}`}><MessageSquare size={13} />View</button></td>
              </tr>
            ))}
          </Table>
        </Panel>
      )}
      <TicketForm open={open} prefill={prefill} onClose={close} onCreated={reload} />
      {view && <UserTicket id={view} onClose={() => setView(null)} onChanged={reload} />}
    </>
  );
}
